import { Link, router } from "@inertiajs/react";
import { Search as SearchIcon } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { search } from "@/routes.js";
import type { SearchResult } from "@/types";

interface SearchProps {
	query: string;
	results: SearchResult[];
	error?: string;
}

export default function Search({ query, results, error }: SearchProps) {
	const [searchQuery, setSearchQuery] = useState(query);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		if (searchQuery.trim()) {
			router.get(search(), { q: searchQuery });
		}
	};

	return (
		<div className="container mx-auto py-8 px-4 max-w-4xl">
			<div className="mb-8">
				<h1 className="text-4xl font-bold mb-2">Genius Fetch</h1>
				<p className="text-muted-foreground">
					Search for artists and discover their songs
				</p>
			</div>

			<form onSubmit={handleSearch} className="mb-8">
				<div className="flex gap-2">
					<div className="relative flex-1">
						<SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							type="text"
							placeholder="Search for an artist..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-10"
						/>
					</div>
					<Button type="submit">Search</Button>
				</div>
			</form>

			{error && (
				<Alert variant="destructive" className="mb-6">
					<AlertTitle>Error</AlertTitle>
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			)}

			{query && !error && (
				<div className="mb-4">
					<p className="text-sm text-muted-foreground">
						{results.length} result{results.length !== 1 ? "s" : ""} for "
						{query}"
					</p>
				</div>
			)}

			<div className="grid gap-4">
				{results.map((result) => (
					<Card key={`${result.type}-${result.id}`}>
						<CardHeader className="flex flex-row items-start gap-4">
							{result.thumbnail && (
								<img
									src={result.thumbnail}
									alt={result.title}
									className="w-16 h-16 rounded object-cover"
								/>
							)}
							<div className="flex-1">
								<CardTitle className="text-lg">{result.title}</CardTitle>
								{result.artist_name && (
									<CardDescription>{result.artist_name}</CardDescription>
								)}
							</div>
						</CardHeader>
						<CardContent>
							<div className="flex gap-2">
								<Button variant="outline" size="sm" asChild>
									<a
										href={result.url}
										target="_blank"
										rel="noopener noreferrer"
									>
										View on Genius
									</a>
								</Button>
								{result.artist_id && (
									<Button variant="outline" size="sm" asChild>
										<Link href={`/artists/${result.artist_id}`}>View Artist</Link>
									</Button>
								)}
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{query && results.length === 0 && !error && (
				<div className="text-center py-12">
					<p className="text-muted-foreground">
						No results found for "{query}"
					</p>
				</div>
			)}
		</div>
	);
}
